import { displayOperationalMonth, indexToOperationalMonth } from './operational-control/operational-start-period-config';
import type { CaptureRegimeActivation } from './prior-period-capture-config';

export type PriorPeriodCaptureStatus = 'CAPTURED' | 'PENDING' | 'INDETERMINATE' | 'NOT_APPLICABLE';

export type PriorPeriodCaptureInput = {
  frequency: unknown;
  monthIndex: number;
  cycleYear: number;
  currentMonthIndex: number | null;
  cutoffMonthIndex: number;
  operationalStartMonthIndex: number;
  expectedMonths: readonly number[];
  monthlyTarget: unknown;
  historyEntry?: Record<string, unknown> | null;
  regimeActivation?: CaptureRegimeActivation | null;
};

const hasProgress = (value: unknown): boolean => {
  if (!Array.isArray(value)) return false;
  return value.some((item) => item?.completed === true
    || [item?.realizado, item?.real, item?.value].some((progress) => Number.isFinite(Number(progress)) && Number(progress) > 0));
};

const isIsoTimestamp = (value: unknown): value is string => typeof value === 'string'
  && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2})$/.test(value)
  && Number.isFinite(Date.parse(value));

const hasCaptureEvidence = (entry: Record<string, unknown>): boolean => {
  if (isIsoTimestamp(entry.captureConfirmedAt)) return true;
  const real = entry.real ?? entry.valor ?? entry.value;
  if (typeof real === 'number' && Number.isFinite(real) && real > 0) return true;
  if (typeof real === 'string' && real.trim() !== '' && Number.isFinite(Number(real)) && Number(real) > 0) return true;
  if (typeof entry.note === 'string' && entry.note.trim().length > 0) return true;
  return hasProgress(entry.checklist) || hasProgress(entry.elements) || hasProgress(entry.activities);
};

export const classifyPriorPeriodCapture = (input: PriorPeriodCaptureInput): PriorPeriodCaptureStatus => {
  const { monthIndex, currentMonthIndex, historyEntry } = input;
  const target = Number(input.monthlyTarget);
  if (input.frequency !== 'Mensual'
    || !Number.isInteger(monthIndex) || monthIndex < 0 || monthIndex > 11
    || currentMonthIndex === null || !Number.isInteger(currentMonthIndex) || currentMonthIndex < 0 || currentMonthIndex > 11
    || monthIndex >= currentMonthIndex
    || !Number.isInteger(input.cutoffMonthIndex) || input.cutoffMonthIndex < -1 || input.cutoffMonthIndex > 11
    || !Number.isInteger(input.operationalStartMonthIndex) || input.operationalStartMonthIndex < 0 || input.operationalStartMonthIndex > 11
    || monthIndex > input.cutoffMonthIndex
    || monthIndex < input.operationalStartMonthIndex
    || !input.expectedMonths.includes(monthIndex)
    || !Number.isFinite(target) || target <= 0) return 'NOT_APPLICABLE';

  const entry = historyEntry || {};
  if (hasCaptureEvidence(entry)) return 'CAPTURED';
  const activation = input.regimeActivation;
  if (activation && Number.isInteger(activation.year)
    && Number.isInteger(activation.monthIndex) && activation.monthIndex >= 0 && activation.monthIndex <= 11
    && (input.cycleYear > activation.year || (input.cycleYear === activation.year && monthIndex >= activation.monthIndex))) return 'PENDING';
  return 'INDETERMINATE';
};

export const canonicalPriorPeriodCaptureKey = (instanceId: string, indicatorId: string, year: number, monthIndex: number): string =>
  JSON.stringify([instanceId, indicatorId, year, displayOperationalMonth(indexToOperationalMonth(monthIndex))]);

export const countUniquePendingPriorPeriods = <T extends { key: string; status: PriorPeriodCaptureStatus }>(
  periods: readonly T[],
): number => new Set(periods.filter((period) => period.status === 'PENDING').map((period) => period.key)).size;


export type PriorCapturePeriod = {
  key: string;
  instanceId: string;
  indicatorId: string;
  indicatorName: string;
  month: string;
  monthLabel: string;
  monthIndex: number;
  year: number;
  cycleYear: number;
  status: PriorPeriodCaptureStatus;
};

export type PriorPeriodCaptureSummary = {
  evaluable: boolean;
  pending: PriorCapturePeriod[];
  indeterminate: PriorCapturePeriod[];
};

// Same 1-based precedence as Control Operativo, but never use a fallback clock.
export const parseCaptureOperationalMonth = (value: unknown): number | null => {
  if (value === null || value === undefined || (typeof value !== 'number' && typeof value !== 'string')
    || (typeof value === 'string' && !value.trim())) return null;
  const month = Number(value);
  if (!Number.isInteger(month)) return null;
  if (month >= 1 && month <= 12) return month - 1;
  return month === 0 ? 0 : null;
};

export const buildPriorPeriodCaptureSummary = (input: {
  instanceId: string;
  indicators: readonly any[];
  cycleYear: number;
  selectedMonthIndex: number;
  currentMonthIndex: number | null;
  lastCompletedMonthIndex: number;
  operationalWindow: { startMonthIndex: number; expectedMonths: readonly number[]; isOperationallyActive: boolean };
  regimeActivation: CaptureRegimeActivation | null;
}): PriorPeriodCaptureSummary => {
  const empty = { evaluable: false, pending: [], indeterminate: [] };
  if (!input.instanceId || input.currentMonthIndex === null
    || !Number.isInteger(input.currentMonthIndex) || input.currentMonthIndex < 0 || input.currentMonthIndex > 11
    || !Number.isInteger(input.selectedMonthIndex) || input.selectedMonthIndex < 0 || input.selectedMonthIndex > 11
    || !Number.isInteger(input.lastCompletedMonthIndex) || input.lastCompletedMonthIndex < -1 || input.lastCompletedMonthIndex > 11) return empty;
  const cutoff = Math.min(input.selectedMonthIndex, input.lastCompletedMonthIndex, input.currentMonthIndex - 1);
  const periods = new Map<string, PriorCapturePeriod>();
  if (input.operationalWindow.isOperationallyActive) {
    for (const indicator of input.indicators) {
      const owner = String(indicator.instance_id || indicator.unified_instance_id || indicator.commission_id || '').trim();
      const indicatorId = String(indicator.id || '').trim();
      if (owner !== input.instanceId || !indicatorId || (indicator.frec ?? indicator.frequency) !== 'Mensual') continue;
      for (let monthIndex = 0; monthIndex <= cutoff; monthIndex += 1) {
        const monthLabel = displayOperationalMonth(indexToOperationalMonth(monthIndex));
        const entries = (Array.isArray(indicator.history) ? indicator.history : [])
          .filter((entry: any) => String(entry?.period || entry?.month || '').trim().toUpperCase() === monthLabel);
        for (const historyEntry of entries) {
          const status = classifyPriorPeriodCapture({
            frequency: 'Mensual', monthIndex, cycleYear: input.cycleYear,
            currentMonthIndex: input.currentMonthIndex, cutoffMonthIndex: cutoff,
            operationalStartMonthIndex: input.operationalWindow.startMonthIndex,
            expectedMonths: input.operationalWindow.expectedMonths,
            monthlyTarget: historyEntry.meta, historyEntry, regimeActivation: input.regimeActivation,
          });
          if (status === 'NOT_APPLICABLE') continue;
          const key = canonicalPriorPeriodCaptureKey(owner, indicatorId, input.cycleYear, monthIndex);
          // A duplicate with real evidence must not become a false pending row.
          if (periods.get(key)?.status === 'CAPTURED') continue;
          periods.set(key, { key, instanceId: owner, indicatorId, indicatorName: indicator.nombre || indicator.name || 'Indicador', month: monthLabel, monthLabel, monthIndex, year: input.cycleYear, cycleYear: input.cycleYear, status });
        }
      }
    }
  }
  return {
    evaluable: true,
    pending: [...periods.values()].filter((period) => period.status === 'PENDING'),
    indeterminate: [...periods.values()].filter((period) => period.status === 'INDETERMINATE'),
  };
};
